import {
  Clock,
  MapPin,
  User,
  Users,
  ClipboardCheck,
  BarChart3,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { DeleteSessionDialog } from './delete-session-dialog';

interface SessionInfoCardProps {
  session: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    coachName?: string | null;
    courtNumber?: number | null;
    sessionType?: string | null;
    seriesId?: string | null;
  };
  isPast: boolean;
  stats: {
    totalPlayers: number;
    presentCount: number;
    evaluatedCount: number;
  };
  formatDate: (dateStr: string) => string;
  formatTime: (time: string) => string;
  onDeleted: () => void;
}

export function SessionInfoCard({
  session,
  isPast,
  stats,
  formatDate,
  formatTime,
  onDeleted,
}: SessionInfoCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">
                {formatDate(session.date)}
              </h1>
              <Badge
                variant={isPast ? 'secondary' : 'default'}
                className={isPast ? '' : 'bg-purple-600'}
              >
                {isPast ? 'Past' : 'Upcoming'}
              </Badge>
            </div>

            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatTime(session.startTime)} - {formatTime(session.endTime)}
              </div>
              {session.coachName && (
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {session.coachName}
                </div>
              )}
              {session.courtNumber && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Court {session.courtNumber}
                </div>
              )}
              {session.sessionType && (
                <Badge variant="outline" className="capitalize">
                  {session.sessionType.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
          </div>

          <DeleteSessionDialog
            sessionId={session.id}
            seriesId={session.seriesId}
            onDeleted={onDeleted}
          />
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-6 mt-4 pt-4 border-t">
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="font-medium">{stats.totalPlayers}</span>
            <span className="text-muted-foreground">
              {isPast ? 'attended' : 'assigned'}
            </span>
          </div>
          {isPast && (
            <>
              <div className="flex items-center gap-2 text-sm">
                <ClipboardCheck className="w-4 h-4 text-green-600" />
                <span className="font-medium">{stats.presentCount}</span>
                <span className="text-muted-foreground">present</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                <span className="font-medium">{stats.evaluatedCount}</span>
                <span className="text-muted-foreground">evaluated</span>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
